import type { VehicleResources } from '../en/vehicle';

export const vehicle: VehicleResources = {
  list: {
    title: 'Fahrzeuge',
    addVehicle: 'Fahrzeug hinzufügen',
    searchPlaceholder: 'Fahrzeuge suchen...',
    loadError: 'Fahrzeuge konnten nicht geladen werden.',
    openRecordFor: 'Fahrzeugakte für {{vehicle}} öffnen',
    result: {
      singular: 'Fahrzeug',
      plural: 'Fahrzeuge',
    },
    viewBar: {
      defaultLabel: 'Alle Fahrzeuge',
    },
    columns: {
      imageAlt: 'Bild',
      chevronAlt: 'Öffnen',
      licensePlate: 'Kennzeichen',
      status: 'Status',
      location: 'Standort',
      drivetrain: 'Antriebsart',
      mileageMeters: 'Kilometerstand',
      currentDriver: 'Fahrer',
      vehicleAgeYears: 'Fahrzeugalter',
      co2Hint: 'WLTP',
    },
    filters: {
      modelHint: 'Zuerst eine Marke wählen, um die Modellliste einzugrenzen.',
      modelTypeHint: 'Zuerst ein Modell wählen, um die Modelltypliste einzugrenzen.',
    },
    footnotes: {
      vehicleAge: 'Aus dem Herstellungsdatum',
    },
    stats: {
      activeInFleet: 'Aktiv im Fuhrpark',
      averageAge: 'Ø Fahrzeugalter',
      highestMileage: 'Höchster Kilometerstand',
      electricShare: 'Anteil Elektrofahrzeuge',
      years: '{{count}} Jahr',
      years_other: '{{count}} Jahre',
      loadError: 'Kennzahlen konnten nicht geladen werden.',
    },
    empty: {
      title: 'Noch keine Fahrzeuge',
      description: 'Fügen Sie Ihr erstes Fahrzeug hinzu, um den Fuhrpark aufzubauen.',
    },
    noResults: {
      title: 'Keine Fahrzeuge gefunden',
      description: 'Passen Sie Suche oder Filter an, um wieder Ergebnisse zu sehen.',
      reset: 'Filter zurücksetzen',
    },
  },

  attributes: {
    brand: 'Marke',
    model: 'Modell',
    modelType: 'Modelltyp',
    vin: 'FIN',
    drivetrain: 'Antriebsart',
    transmission: 'Getriebe',
    color: 'Außenfarbe',
    imageUrl: 'Foto',
    mileageMeters: 'Kilometerstand',
    mileageRecordedOn: 'Kilometerstand erfasst am',
    firstRegistration: 'Erstzulassung',
    createdAt: 'Erstellt',
    createdBy: 'Erstellt von',
    updatedAt: 'Zuletzt geändert',
    updatedBy: 'Geändert von',
    status: 'Status',
    licensePlate: 'Kennzeichen',
    countryCode: 'Land',
    currentDriver: 'Aktueller Fahrer',
    costCenter: 'Kostenstelle',
    hsn: 'HSN',
    tsn: 'TSN',
    productionDate: 'Herstellungsdatum',
    bodyType: 'Karosserieform',
    doors: 'Türen',
    seats: 'Sitzplätze',
    vehicleClass: 'Fahrzeugklasse',
    primaryEnergy: 'Primärenergie',
    secondaryEnergy: 'Sekundärenergie',
    secondaryDrive: 'Zweitantrieb',
    highVoltageSystem: 'Hochvoltsystem',
    driveLayout: 'Radantrieb',
    systemPowerWatts: 'Systemleistung',
    enginePowerWatts: 'Motorleistung',
    displacementCm3: 'Hubraum',
    acChargingPort: 'AC-Ladeanschluss',
    maxAcChargingPowerWatts: 'AC-Ladeleistung max.',
    dcChargingPort: 'DC-Ladeanschluss',
    maxDcChargingPowerWatts: 'DC-Ladeleistung max.',
    testCycle: 'Messverfahren',
    co2CombinedMgKm: 'CO₂ kombiniert',
    emissionClass: 'Schadstoffklasse',
    depletedBatteryConsumptionMl: 'Verbrauch bei entladener Batterie',
    allElectricConsumptionWh: 'Stromverbrauch rein elektrisch',
    electricRangeMeters: 'Elektrische Reichweite',
    heatPump: 'Wärmepumpe',
    adBlueSystemPresent: 'AdBlue-System',
    adBlueTankMl: 'AdBlue-Tankvolumen',
    lengthMinMm: 'Länge (min.)',
    lengthMaxMm: 'Länge (max.)',
    widthWithoutMirrorsMinMm: 'Breite ohne Spiegel (min.)',
    widthWithoutMirrorsMaxMm: 'Breite ohne Spiegel (max.)',
    widthIncludingMirrorsMm: 'Breite inkl. Spiegel',
    heightMinMm: 'Höhe (min.)',
    heightMaxMm: 'Höhe (max.)',
    kerbWeightMinGrams: 'Leergewicht (min.)',
    kerbWeightMaxGrams: 'Leergewicht (max.)',
    grossMassGrams: 'Zulässige Gesamtmasse',
    payloadMinGrams: 'Nutzlast (min.)',
    payloadMaxGrams: 'Nutzlast (max.)',
    towingCapacityBrakedGrams: 'Anhängelast gebremst',
    towingCapacityUnbrakedGrams: 'Anhängelast ungebremst',
    grossCombinationMassGrams: 'Zulässige Zuggesamtmasse',
    noseWeightGrams: 'Stützlast',
    roofLoadGrams: 'Dachlast',
    bootNormalMl: 'Kofferraum normal',
    bootMaxMl: 'Kofferraum maximal',
    frunkMl: 'Frunk',
    totalRangeMeters: 'Gesamtreichweite',
  },

  storageContainer: {
    petrol: 'Benzintank',
    diesel: 'Dieseltank',
    lpg: 'Autogastank',
    cng: 'Erdgasspeicher',
    lng: 'Flüssigerdgasspeicher',
    ethanol: 'Ethanoltank',
    electricity: 'Batterie',
  },

  status: {
    active: 'Aktiv',
    inactive: 'Inaktiv',
  },

  drivetrain: {
    petrol: 'Benzin',
    diesel: 'Diesel',
    petrolMildHybrid: 'Benzin Mild-Hybrid',
    dieselMildHybrid: 'Diesel Mild-Hybrid',
    petrolFullHybrid: 'Benzin Voll-Hybrid',
    dieselFullHybrid: 'Diesel Voll-Hybrid',
    plugInHybridPetrol: 'Plug-in-Hybrid Benzin',
    plugInHybridDiesel: 'Plug-in-Hybrid Diesel',
    batteryElectricBev: 'Batterieelektrisch (BEV)',
    lpg: 'Autogas (LPG)',
    cng: 'Erdgas (CNG)',
  },

  bodyType: {
    saloon: 'Limousine',
    estate: 'Kombi',
    hatchback: 'Schrägheck',
    suv: 'SUV',
    coupe: 'Coupé',
    convertible: 'Cabrio',
    roadster: 'Roadster',
    van: 'Van',
    minibus: 'Kleinbus',
    panelVan: 'Kastenwagen',
    pickUp: 'Pick-up',
    other: 'Sonstige',
  },

  vehicleClass: {
    M1: 'Pkw',
    M1G: 'Gelände-Pkw',
    N1: 'Transporter / leichter Lkw bis 3,5 t',
    N1G: 'Gelände-Transporter bis 3,5 t',
    N2: 'Lkw > 3,5–12 t',
    N2G: 'Gelände-Lkw > 3,5–12 t',
    N3: 'Lkw > 12 t',
    N3G: 'schwerer Gelände-Lkw > 12 t',
    M2: 'Kleinbus / Bus bis 5 t',
    M2G: 'Gelände-Kleinbus',
    M3: 'Bus über 5 t',
    M3G: 'Gelände-Bus',
  },

  energyCarrier: {
    petrol: 'Benzin',
    diesel: 'Diesel',
    lpg: 'Autogas (LPG)',
    cng: 'Erdgas (CNG)',
    lng: 'Flüssigerdgas (LNG)',
    ethanol: 'Ethanol',
    electricity: 'Strom',
  },

  boolean: {
    yes: 'Ja',
    no: 'Nein',
  },

  driveLayout: {
    front: 'Front',
    rear: 'Heck',
    allWheel: 'Allrad',
  },

  acChargingPort: {
    'Type 2': 'Typ 2',
    'Type 1': 'Typ 1',
    none: 'Kein',
  },

  dcChargingPort: {
    CCS: 'CCS',
    CHAdeMO: 'CHAdeMO',
    none: 'Kein',
  },

  testCycle: {
    WLTP: 'WLTP',
    NEDC: 'NEFZ',
  },

  grantRole: {
    viewer: 'Betrachter',
    owner: 'Eigentümer',
    keeper: 'Halter',
  },

  countryCode: {
    DE: 'Deutschland',
    FR: 'Frankreich',
    NL: 'Niederlande',
    CH: 'Schweiz',
  },

  power: {
    withHp: '{{kw}} kW ({{hp}} PS)',
  },

  access: {
    noAccess: 'Kein Zugriff',
    hiddenByGrant: {
      label: 'kein Zugriff (Grant)',
      tooltip: 'Ebene nicht zugänglich: dem Mandanten fehlt der erforderliche Grant',
    },
    hiddenByRole: {
      label: 'kein Zugriff (Rolle)',
      tooltip: 'Ihre Rolle gewährt keinen Zugriff auf diese Attributgruppe',
    },
  },

  licensePlate: {
    aria: 'Kennzeichen {{plate}} ({{country}})',
    ariaNoCountry: 'Kennzeichen {{plate}}',
    missing: 'Kein Kennzeichen',
    country: {
      DE: 'Deutschland',
      FR: 'Frankreich',
      NL: 'Niederlande',
      CH: 'Schweiz',
    },
  },

  // The four-step create assistant (manage-vehicles-14, PRD "Create a vehicle").
  wizard: {
    page: {
      title: 'Fahrzeug anlegen',
      noPermission: 'Sie haben keine Berechtigung, in diesem Mandanten Fahrzeuge zu erstellen.',
    },
    toast: {
      created: 'Fahrzeug angelegt',
      createFailed: 'Das Fahrzeug konnte nicht angelegt werden.',
    },
    nav: {
      stepCount: 'Schritt {{current}} von {{total}}',
      next: 'Weiter',
      back: 'Zurück',
      cancel: 'Abbrechen',
      create: 'Fahrzeug anlegen',
      creating: 'Wird angelegt…',
    },
    step: {
      operation: { title: 'Betrieb' },
      identity: { title: 'Identität' },
      vehicle: { title: 'Fahrzeug' },
      technicalDetails: { title: 'Technische Details' },
    },
    deferredMandatory:
      '{{count}} technische Pflichtangabe fehlt noch. Das Fahrzeug lässt sich trotzdem anlegen — nachtragen können Sie sie jederzeit in der Fahrzeugakte.',
    deferredMandatory_other:
      '{{count}} technische Pflichtangaben fehlen noch. Das Fahrzeug lässt sich trotzdem anlegen — nachtragen können Sie sie jederzeit in der Fahrzeugakte.',
    drivetrainMissing: 'Bitte zuerst das Antriebskonzept wählen.',
    unsavedChanges: {
      title: 'Dieses Fahrzeug verwerfen?',
      description: 'Was Sie eingegeben haben, wurde nicht gespeichert.',
      discard: 'Verwerfen',
      keepEditing: 'Weiter bearbeiten',
    },
  },

  detail: {
    fallbackTitle: 'Fahrzeugdetails',
    back: 'Alle Fahrzeuge',
    loadError: 'Fahrzeug konnte nicht geladen werden.',
    empty: 'Keine Details für dieses Fahrzeug verfügbar.',
    actions: {
      edit: 'Bearbeiten',
      activate: 'Fahrzeug aktivieren',
      deactivate: 'Fahrzeug deaktivieren',
    },
    toast: {
      updated: 'Fahrzeug aktualisiert',
      updateFailed: 'Das Fahrzeug konnte nicht gespeichert werden. Bitte erneut versuchen.',
      activated: 'Fahrzeug aktiviert',
      activateFailed: 'Das Fahrzeug konnte nicht aktiviert werden. Bitte erneut versuchen.',
      deactivated: 'Fahrzeug deaktiviert',
      deactivateFailed: 'Das Fahrzeug konnte nicht deaktiviert werden. Bitte erneut versuchen.',
    },
    deactivateConfirm: {
      title: '{{vehicle}} deaktivieren?',
      description: 'Deaktivierte Fahrzeuge verlassen sofort den Buchungspool.',
      endAssignment: {
        label: 'Laufende Fahrerzuweisung beenden',
        description: 'Der Fahrer wird für andere Fahrzeuge verfügbar.',
      },
      cancel: 'Abbrechen',
      confirm: 'Deaktivieren',
      deactivating: 'Wird deaktiviert…',
    },
    requiredMarker: 'Pflichtfeld',
    findingIcon: {
      error: 'Fehler',
      warning: 'Hinweis',
    },
    modelPath: {
      chooseMakeFirst: 'Erst Marke wählen',
      chooseModelFirst: 'Erst Modell wählen',
    },
    blockedSave: {
      otherTab: 'Offener Befund unter „{{tab}}“',
    },
    unsavedChanges: {
      title: 'Änderungen verwerfen?',
      description: 'Ihre Änderungen an diesem Fahrzeug wurden nicht gespeichert.',
      discard: 'Verwerfen',
      keepEditing: 'Weiter bearbeiten',
    },
    warnings: {
      mileageUnusuallyHigh: 'Ungewöhnlich hoch — über 2.000.000 km. Bitte prüfen.',
      mileageBelowLastReading: 'Niedriger als der zuletzt erfasste Stand.',
      powerUnusuallyHigh: 'Ungewöhnlich hoch für ein Flottenfahrzeug. Bitte prüfen.',
      displacementUnusuallyLarge: 'Ungewöhnlich groß. Bitte prüfen.',
      electricVehicleMissingConsumption: 'Bei einem Elektrofahrzeug sollte der Stromverbrauch erfasst sein.',
      emissionClassNotRecorded: 'Schadstoffklasse ist noch nicht erfasst.',
      productionDateAfterFirstRegistration: 'Liegt nach der Erstzulassung.',
      combinationMassBelowGrossMass: 'Liegt unter der zulässigen Gesamtmasse des Fahrzeugs allein.',
      widthWithMirrorsBelowWithout: 'Mit Spiegeln ist ein Fahrzeug nicht schmaler als ohne.',
      bootMaxBelowNormal: 'Das maximale Volumen liegt unter dem normalen.',
      noWheelTireCombination: 'Es ist noch keine Rad-/Reifenkombination erfasst.',
    },
    tabs: {
      operation: 'Betrieb',
      masterData: 'Stammdaten',
      driver: 'Fahrer',
    },
    notFound: {
      title: 'Fahrzeug nicht gefunden',
      body: 'Kein Fahrzeug mit der ID {{vehicle}}',
      back: 'Zurück zur Liste',
    },
    hero: {
      grantRole: 'Berechtigungsrolle',
      orgUnit: 'Organisationseinheit',
    },
    sections: {
      operation: { title: 'Betrieb' },
      identification: { title: 'Identifikation' },
      vehicle: { title: 'Fahrzeug' },
      drivetrainEngine: { title: 'Antrieb & Motor' },
      energyStorage: { title: 'Energiespeicher' },
      highVoltageCharging: { title: 'Hochvolt & Laden' },
      consumptionRange: { title: 'Verbrauch & Reichweite' },
      dimensionsWeights: { title: 'Abmessungen & Gewichte' },
      trailerLoads: { title: 'Anhänger & Lasten' },
      loadCompartment: { title: 'Laderaum' },
      wheelsTires: { title: 'Räder & Reifen' },
      hasError: 'Enthält einen Fehler',
      hasWarning: 'Enthält einen Hinweis',
    },
    energyStorage: {
      noDrivetrain: 'Erst mit dem Antriebskonzept steht fest, welche Speicher es gibt.',
    },
    consumptionRange: {
      noDrivetrain: 'Erst mit dem Antriebskonzept steht fest, welche Energieträger das Fahrzeug führt.',
    },
    audit: {
      created: 'Erstellt {{date}}',
      createdBy: 'Erstellt {{date}} von {{user}}',
      updated: 'zuletzt geändert {{date}}',
      updatedBy: 'zuletzt geändert {{date}} von {{user}}',
    },
  },

  wheelTire: {
    noCombination: 'Keine Kombination erfasst.',
    add: 'Kombination hinzufügen',
    remove: 'Entfernen',
    defaultAxlePosition: 'Achse 1+2',
    columns: {
      axlePosition: 'Achse',
      tireSize: 'Reifengröße',
      loadSpeed: 'Load/Speed',
      loadIndex: 'Load Index',
      speedIndex: 'Speed Index',
      markings: 'Kennzeichnungen',
      rimSize: 'Felge',
      offsetEtMm: 'ET (mm)',
    },
  },

  form: {
    required: 'Erforderlich',
    cancel: 'Abbrechen',
    selectPlaceholder: '—',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
  },

  driverAssignment: {
    title: 'Aktuelle Zuweisung',
    empty: 'Aktuell kein Fahrer zugewiesen',
    assign: 'Fahrer zuweisen',
    end: 'Zuweisung beenden',
    inactiveVehicleReason: 'Deaktivierten Fahrzeugen kann kein Fahrer zugewiesen werden.',
    columns: { driver: 'Fahrer', date: 'Zuweisungsdatum' },
    dialog: {
      assign: {
        title: 'Fahrer zuweisen',
        driverLabel: 'Fahrer',
        driverPlaceholder: 'Fahrer auswählen…',
        driverLoading: 'Fahrer werden geladen…',
        noDriverAvailable: 'Kein freier Fahrer verfügbar',
        startLabel: 'Beginn',
        cancel: 'Abbrechen',
        confirm: 'Fahrer zuweisen',
        assigning: 'Wird zugewiesen…',
      },
      end: {
        title: 'Zuweisung für {{driver}} beenden?',
        description: 'Der Fahrer wird für andere Fahrzeuge verfügbar.',
        cancel: 'Abbrechen',
        confirm: 'Zuweisung beenden',
        ending: 'Wird beendet…',
      },
    },
    toast: {
      assigned: 'Fahrer zugewiesen',
      ended: 'Zuweisung beendet',
      failed: 'Zuweisung konnte nicht gespeichert werden. Bitte erneut versuchen.',
    },
  },
};
